import React, { useCallback, useRef, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { AlertCircle, Check, CheckCheck, Clock, FileText, Download, MessageSquare, ClipboardCopy, Eye, Loader2, Forward, Trash2 } from 'lucide-react';
import { AudioMessage } from './AudioMessage';
import { VideoMessage } from './VideoMessage';
import { renderTextWithLinks } from '../utils/renderTextWithLinks';
import { renderEmojiWithFallback } from '../utils/emojiUtils';
import { toast } from '../hooks/use-toast';
import api from '../services/api';
import InteractiveMessage from './InteractiveMessage';
import logoImg from '../assets/Sintis_Logo.png';

export interface MensagemItemProps {
  msg: any;
  user: any;
  conversa: any;
  selectedMessage: string | null;
  onContextMenu: (e: React.MouseEvent, id: string, isAtendente: boolean) => void;
  onOpenPreview: (payload: { type: 'imagem' | 'video' | 'documento' | 'sticker'; url: string; name?: string; readonly?: boolean; file?: File; crop?: any; zoom?: number; croppedAreaPixels?: any }) => void;
  onStartChat?: (telefone: string) => void;
  onReply?: (msg: any) => void;
  ticketData?: any;
}

const getInitials = (name: string | null | undefined = '') => {
  return (name ?? '')
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'C';
};

const getFileUrl = (path?: string) => {
  if (!path) return '';
  if (/^https?:\/\//.test(path)) return path;
  let API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');
  if (!path.startsWith('/')) path = '/' + path;
  // console.log('URL processada:', `${API_URL}${path}`);
  return `${API_URL}${path}`;
};

const extrairTelefoneDeVCard = (raw: any): string => {
  if (!raw) return '';
  const str = String(raw);
  const digits = str.replace(/\D/g, '');
  if (digits.length >= 8 && digits.length <= 15) return digits;
  const waid = str.match(/waid=(\d+)/);
  if (waid && waid[1]) return waid[1];
  const tel = str.match(/TEL[^:]*:\s*([+\d\s-]+)/);
  if (tel && tel[1]) {
    const telDigits = tel[1].replace(/\D/g, '');
    if (telDigits.length >= 8 && telDigits.length <= 15) return telDigits;
  }
  const anyDigits = str.match(/\d[\d\s().-]{7,14}\d/);
  if (anyDigits) {
    const found = anyDigits[0].replace(/\D/g, '');
    if (found.length >= 8 && found.length <= 15) return found;
  }
  return '';
};

const handleViewPDF = (url: string, filename: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const handleDownload = async (url: string, filename: string) => {
  console.log('Tentando baixar:', { url, filename });

  try {
    // Abordagem 1: Para imagens - usa canvas para converter e baixar
    if (filename.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      console.log('Tentando download de imagem via canvas...');

      const img = new Image();
      img.crossOrigin = 'anonymous'; // Tenta permitir CORS

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');

          if (ctx) {
            ctx.drawImage(img, 0, 0);

            // Converte para blob e baixa
            canvas.toBlob((blob) => {
              if (blob) {
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
                console.log('Download via canvas realizado');
              }
            }, 'image/png');
          }
        } catch (error) {
          console.error('Erro no canvas:', error);
          // Fallback para XMLHttpRequest
          downloadViaXHR(url, filename);
        }
      };

      img.onerror = () => {
        console.error('Erro ao carregar imagem');
        downloadViaXHR(url, filename);
      };

      img.src = url;

    } else {
      // Para outros arquivos, usa XMLHttpRequest
      downloadViaXHR(url, filename);
    }
  } catch (error) {
    console.error('Erro geral:', error);
    // Fallback final
    window.open(url, '_blank');
  }
};

// Função auxiliar com XMLHttpRequest
const downloadViaXHR = (url: string, filename: string) => {
  console.log('Tentando download via XMLHttpRequest...');

  const xhr = new XMLHttpRequest();
  xhr.open('GET', url, true);
  xhr.responseType = 'blob';

  xhr.onload = function () {
    if (xhr.status === 200) {
      const blob = xhr.response;
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.target = '_blank';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      console.log('Download via XHR realizado');
    } else {
      console.error('XHR falhou:', xhr.status);
      window.open(url, '_blank');
    }
  };

  xhr.onerror = () => {
    console.error('Erro de rede XHR');
    window.open(url, '_blank');
  };

  xhr.send();
};

const handleCopy = (text: string) => {
  navigator.clipboard.writeText(text);
  toast({
    title: 'Copiado',
    description: 'Texto copiado para a área de transferência',
    variant: 'default',
  });
};

// Função auxiliar para extrair nome do arquivo do caminho
const getFileNameFromPath = (path: string | undefined) => {
  if (!path) return undefined;
  // Remove query params e extrai apenas o nome do arquivo
  const cleanPath = path.split('?')[0];
  const fileName = cleanPath.split('/').pop();
  if (fileName) {
    // Remove o timestamp token do início (formato: 1234567890-xxx.ext)
    return fileName.replace(/^\d+-/, '');
  }
  return fileName;
};

// Componente para preview de arquivos de texto
// const TextFilePreview: React.FC<{ url: string; name?: string }> = ({ url, name }) => {
//   const [content, setContent] = React.useState<string>('Carregando...');
//   const [error, setError] = React.useState<string>('');

//   React.useEffect(() => {
//     fetch(url)
//       .then(response => response.text())
//       .then(text => {
//         setContent(text);
//         setError('');
//       })
//       .catch(err => {
//         console.error('Erro ao carregar arquivo de texto:', err);
//         setError('Erro ao carregar arquivo');
//         setContent('');
//       });
//   }, [url]);

//   if (error) {
//     return (
//       <div className="rounded-md max-w-xs my-1 bg-red-50 border border-red-200 p-4" style={{ width: 220, minHeight: 100 }}>
//         <div className="text-red-600 text-xs text-center">{error}</div>
//       </div>
//     );
//   }

//   return (
//     <div className="rounded-md max-w-xs my-1 bg-white border border-gray-200 p-4 overflow-auto" style={{ width: 220, minHeight: 100, maxHeight: 180 }}>
//       <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words">{content}</pre>
//     </div>
//   );
// };

const MensagemItem: React.FC<MensagemItemProps> = React.memo(({ msg, user, conversa, selectedMessage, onContextMenu, onOpenPreview, onStartChat, onReply, ticketData }) => {
  const isAtendente = msg.remetenteTipo === 'atendente';
  const isSistema = msg.remetenteTipo === 'sistema';

  // --- Swipe to Reply (mobile) ---
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const SWIPE_THRESHOLD = 60; // pixels to trigger reply

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    setIsSwiping(false);
    setSwipeOffset(0);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = Math.abs(e.touches[0].clientY - touchStartY.current);
    // Only track horizontal swipe if it's more horizontal than vertical
    if (dy > 10 && dy > Math.abs(dx)) return;
    if (Math.abs(dx) < 5) return;
    // Allow swipe right for client messages, swipe left for agent messages
    const direction = isAtendente ? -1 : 1;
    const validSwipe = isAtendente ? dx < 0 : dx > 0;
    if (validSwipe) {
      e.preventDefault();
      const capped = Math.min(Math.abs(dx), SWIPE_THRESHOLD + 20);
      setSwipeOffset(direction * capped);
      setIsSwiping(true);
    }
  }, [isAtendente]);

  const handleTouchEnd = useCallback(() => {
    if (Math.abs(swipeOffset) >= SWIPE_THRESHOLD) {
      if (onReply && !msg.deletedAt) {
        onReply(msg);
      }
    }
    // Animate back
    setSwipeOffset(0);
    setIsSwiping(false);
  }, [swipeOffset, onReply, msg]);

  // Verifica se a mensagem foi iniciada antes do atendimento
  const isMensagemAntesDoAtendimento = useCallback(() => {
    if (!ticketData) return false;

    // Se o ticket está pendente, todas as mensagens são consideradas como antes do atendimento
    return ticketData.status === 'pendente';
  }, [ticketData]);

  const handleContext = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      // Bloqueia o menu de contexto se a mensagem foi antes do atendimento
      if (isMensagemAntesDoAtendimento()) {
        toast({
          title: 'Ação não permitida',
          description: 'Esta mensagem foi recebida antes do início do atendimento. Ações não estão disponíveis.',
          variant: 'destructive',
        });
        return;
      }

      if (!msg.deletedAt) onContextMenu(e, msg.id, isAtendente);
    },
    [msg.id, msg.deletedAt, isAtendente, onContextMenu, isMensagemAntesDoAtendimento]
  );

  const renderStatusIcon = () => {
    if (!isAtendente) return null;
    // Usar statusEntrega do backend, ou status do frontend, ou mapear falhou para erro
    const backendStatus = msg.statusEntrega || msg.status;
    let displayStatus = backendStatus;

    // Mapear 'falhou' (backend) para 'erro' (frontend) se necessário
    if (backendStatus === 'falhou') {
      displayStatus = 'erro';
    }

    switch (displayStatus) {
      case 'enviando':
        return <Clock size={16} className="text-gray-400 dark:text-gray-500" />;
      case 'enviada':
        return <Check size={16} className="text-gray-400 dark:text-gray-500" />;
      case 'entregue':
        return <CheckCheck size={16} className="text-gray-400 dark:text-gray-500" />;
      case 'lida':
        return <CheckCheck size={16} className="text-blue-500" />;
      case 'erro':
      case 'falhou':
        return <AlertCircle size={16} className="text-red-500" />;
      default:
        return null;
    }
  };

  // Estado para download sob demanda de mídia em grupos (não-imagens)
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleBaixarMidia = useCallback(async (msgId: string) => {
    setDownloadingId(msgId);
    try {
      await api.post(`/mensagens/${msgId}/baixar-midia`);
    } catch (error: any) {
      console.error('[MENSAGEM] Erro ao baixar mídia:', error);
    } finally {
      setDownloadingId(null);
    }
  }, []);

  if (isSistema) {
    const texto = msg.conteudo ?? msg.texto ?? '';
    return (
      <div className="flex justify-center my-2">
        <Badge variant="secondary" className="text-xs px-2 py-1">
          {texto}
        </Badge>
      </div>
    );
  }

  const tipo = msg.tipo;
  const texto =
    tipo === 'audio' || tipo === 'video' || tipo === 'imagem' || tipo === 'documento' || tipo === 'sticker'
      ? (msg.conteudo?.startsWith('/uploads/') ? getFileUrl(msg.conteudo) : '')
      : msg.conteudo ?? msg.texto ?? '';
  const caption = msg.caption ?? '';
  // Texto para o botão "copiar": o conteúdo em mensagens de texto, ou só a legenda em mídias
  const textoCopiavel = tipo === 'texto'
    ? texto
    : ['imagem', 'video', 'documento', 'sticker'].includes(tipo) ? caption : '';
  // Em grupos o remetente real vem em remetenteContato (tempo real e histórico); `contato` no tempo real é o próprio grupo
  const remetenteGrupo = msg.remetenteContato || msg.contatoRemetente || msg.contato;
  const telefoneRemetente = String(remetenteGrupo?.telefone || '');
  const telefoneRemetenteFmt = /^\d{10,15}$/.test(telefoneRemetente) ? `+${telefoneRemetente}` : '';
  const nomeRemetenteBruto = (remetenteGrupo?.nome || '').trim();
  const nomeRemetente = nomeRemetenteBruto && nomeRemetenteBruto !== 'Desconhecido'
    ? nomeRemetenteBruto
    : (telefoneRemetenteFmt || 'Desconhecido');
  const horaFormatada = format(new Date(msg.timestamp), 'HH:mm', { locale: ptBR });
  // Detecção robusta de tipo de arquivo
  const lower = (v?: string) => (typeof v === 'string' ? v.toLowerCase() : '');
  const fileName = getFileNameFromPath(texto);

  // Verifica se é PDF baseado em múltiplos critérios
  const isPdf =
    tipo === 'documento' && (
      lower(texto).includes('.pdf') ||
      lower(fileName).endsWith('.pdf') ||
      lower(msg?.mimeType).includes('pdf') ||
      lower(msg?.mimetype).includes('pdf') ||
      lower(msg?.contentType).includes('pdf')
    );

  // Verifica se é imagem para tratamento especial
  const isImage =
    tipo === 'imagem' || (
      tipo === 'documento' && (
        lower(fileName).match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/) ||
        lower(msg?.mimeType).startsWith('image/') ||
        lower(msg?.mimetype).startsWith('image/') ||
        lower(msg?.contentType).startsWith('image/')
      )
    );

  // Detecção de arquivos que permitem preview (apenas PDF)
  const isPreviewDoc = tipo === 'documento' && isPdf;

  return (
    <div
      className={`flex mb-2 ${isAtendente ? 'justify-end' : 'justify-start'} ${msg.deletedAt ? 'opacity-70' : ''} relative overflow-visible`}
      data-message-id={msg.id}
      data-api-message-id={msg.apiMessageId}
      data-evolution-message-id={msg.evolutionMessageId}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        transform: `translateX(${swipeOffset}px)`,
        transition: isSwiping ? 'none' : 'transform 0.25s ease-out',
      }}
    >
      {/* Swipe reply icon */}
      {isSwiping && Math.abs(swipeOffset) > 15 && (
        <div
          className={`absolute top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-slate-600 shadow pointer-events-none transition-opacity ${
            Math.abs(swipeOffset) >= SWIPE_THRESHOLD ? 'opacity-100 scale-110' : 'opacity-70'
          } ${isAtendente ? 'right-full mr-2' : 'left-full ml-2'}`}
          style={{ transform: `translateY(-50%) scale(${Math.min(Math.abs(swipeOffset) / SWIPE_THRESHOLD, 1.1)})` }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600 dark:text-gray-300">
            <polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/>
          </svg>
        </div>
      )}
      {/* Avatar do remetente para mensagens de grupo (lado esquerdo) */}
      {msg.isGrupo && !isAtendente && remetenteGrupo && (
        <Avatar className="h-8 w-8 flex-shrink-0 mr-2">
          <AvatarImage src={getFileUrl(remetenteGrupo.avatarUrl)} alt={nomeRemetente} />
          <AvatarFallback className="bg-gray-300 dark:bg-slate-600 text-white text-xs">
            {getInitials(nomeRemetente)}
          </AvatarFallback>
        </Avatar>
      )}

      {msg.remetenteTipo !== 'cliente' && Array.isArray(msg.reactions) && msg.reactions.length > 0 && (
        <div className="flex items-center justify-end mb-1">
          {msg.reactions.map((reaction: any, index: number) => (
            <span key={index} className="mr-1">
              {renderEmojiWithFallback(reaction.conteudo)}
            </span>
          ))}
        </div>
      )}

      <div
        onContextMenu={handleContext}
        className={`relative max-w-[75%] md:max-w-[65%] rounded-lg px-3 py-1.5 shadow-sm text-sm group ${!isAtendente ? 'bg-white dark:bg-slate-700 rounded-bl-none' : 'bg-[#dcf8c6] dark:bg-green-900/80 rounded-br-none'
          } ${selectedMessage === msg.id ? 'bg-yellow-100 dark:bg-yellow-900/50' : ''}`}
      >
        {/* Header para mensagens de grupo - mostrar informações do remetente dentro do balão */}
        {msg.isGrupo && !isAtendente && remetenteGrupo && (
          <div className="flex items-center gap-2 mb-1">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                  {nomeRemetente}
                </span>
                {telefoneRemetenteFmt && nomeRemetente !== telefoneRemetenteFmt && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {telefoneRemetenteFmt}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {msg.mensagemRespondida && msg.mensagemRespondida.tipo !== 'reaction' && (
          <div
            className="mb-1 p-2 text-xs bg-gray-100 dark:bg-gray-700 rounded border-l-2 border-blue-500 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
            onClick={() => {
              const el = document.querySelector(`[data-message-id="${msg.mensagemRespondida.id}"]`);
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.classList.add('ring-2', 'ring-blue-500', 'ring-opacity-50');
                setTimeout(() => el.classList.remove('ring-2', 'ring-blue-500', 'ring-opacity-50'), 2000);
              }
            }}
          >
            <div className="font-medium text-blue-600 dark:text-blue-400 truncate w-[150px]">
              {msg.mensagemRespondida.remetenteTipo === 'atendente' ? user?.nome : conversa?.contato?.nome}
            </div>
            <div className=" break-words whitespace-pre-wrap max-w-full sm:max-w-[300px]">
              {msg.mensagemRespondida.tipo === 'texto'
                ? (msg.mensagemRespondida.conteudo.length > 50 ? String(msg.mensagemRespondida.conteudo).slice(0, 50) + '...' : String(msg.mensagemRespondida.conteudo)) ?? ''
                : `[${String(msg.mensagemRespondida.tipo).charAt(0).toUpperCase()}${String(msg.mensagemRespondida.tipo).slice(1)}]`}
            </div>
          </div>
        )}

        {msg.deletedAt && (
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 italic">
            <Trash2 size={14} className="flex-shrink-0" />
            <span className="italic font-medium">Mensagem apagada</span>
            <span className="text-[11px] opacity-75 not-italic">
              em {format(new Date(msg.deletedAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
            </span>
          </div>
        )}

        {msg.dataEdicao && (
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 italic mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            <span className="italic text-xs">Editada em {format(new Date(msg.dataEdicao), 'HH:mm', { locale: ptBR })}</span>
          </div>
        )}

        {msg.isForwarded && (
          <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 italic mb-1">
            <Forward size={12} />
            <span className="text-[11px]">Encaminhada</span>
          </div>
        )}

        {msg.conteudoOriginal && msg.conteudoOriginal !== msg.conteudo && (
          <div className="mb-2 p-2 bg-gray-50 dark:bg-gray-800 rounded border-l-2 border-orange-400">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Original:</div>
            <p className="text-gray-600 dark:text-gray-300 line-through text-sm break-words whitespace-pre-wrap">
              {renderTextWithLinks(msg.conteudoOriginal)}
            </p>
          </div>
        )}

        {tipo === 'texto' && texto && (
          <p className="text-gray-800 dark:text-gray-100 break-words whitespace-pre-wrap">
            {renderTextWithLinks(texto)}
          </p>
        )}

        {tipo === 'pix' && texto && (
          (() => {
            try {
              const pixData = JSON.parse(texto);
              const { key, key_type, merchant_name, amount, currency } = pixData;
              return (
                <div className="bg-white dark:bg-slate-700 rounded-lg p-4 border border-green-200 dark:border-green-800 max-w-full sm:max-w-xs">
                  <div className="flex items-center gap-2 mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6zm4 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium text-green-700 dark:text-green-300">Pagamento PIX</span>
                  </div>
                  {merchant_name && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Beneficiário</p>
                      <p className="text-sm font-medium dark:text-green-300">{merchant_name}</p>
                    </div>
                  )}
                  {amount > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Valor</p>
                      <p className="text-lg font-bold">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: currency || 'BRL' }).format(amount)}
                      </p>
                    </div>
                  )}
                  <div className="bg-gray-50 dark:bg-slate-800 p-3 rounded border border-gray-200 dark:border-gray-700 mb-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{key_type || 'Chave'}</p>
                    <code className="text-sm font-mono break-all dark:text-green-300">{key}</code>
                  </div>
                  <div className="flex justify-center">
                    <Button variant="outline" className="dark:text-white" onClick={() => handleCopy(key)}><ClipboardCopy className="mr-1" /> Copiar</Button>
                  </div>
                </div>
              );
            } catch {
              return (
                <div className="bg-white dark:bg-slate-700 rounded-lg p-4 border border-green-200 dark:border-green-800 max-w-full sm:max-w-xs">
                  <div className="flex items-center gap-2 mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6zm4 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium text-green-700 dark:text-green-300">Chave PIX</span>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-800 p-3 rounded border border-gray-200 dark:border-gray-700 mb-3">
                    <code className="text-sm font-mono break-all">{texto}</code>
                  </div>
                </div>
              );
            }
          })()
        )}

        {tipo === 'audio' && texto && <AudioMessage id={msg.id} audioSrc={texto} status={msg.status || msg.statusEntrega} />}

        {/* Placeholder para mídia de grupo (download sob demanda) */}
        {['imagem', 'video', 'documento', 'audio', 'sticker'].includes(tipo) && !texto && conversa?.contato?.telefone?.includes('@g.us') && (
          <div className="flex flex-col items-center gap-2 my-1 p-3 rounded-md bg-gray-100 dark:bg-slate-800 border border-dashed border-gray-300 dark:border-gray-600 min-w-[180px]">
            <div className="relative w-16 h-16 overflow-hidden rounded-full">
              <img
                src={logoImg}
                alt="SynTicket"
                className="w-full h-full object-contain opacity-50"
              />
              <div className="absolute inset-0 backdrop-blur-sm" />
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Mídia de grupo</span>
            <Button
              variant="outline"
              size="sm"
              disabled={downloadingId === msg.id}
              onClick={() => handleBaixarMidia(msg.id)}
            >
              {downloadingId === msg.id ? (
                <>
                  <Loader2 size={14} className="mr-1 animate-spin" /> Baixando
                </>
              ) : (
                <>
                  <Download size={14} className="mr-1" /> Baixar
                </>
              )}
            </Button>
            {caption && (
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">{caption}</p>
            )}
          </div>
        )}

        {tipo === 'video' && texto && (
          <div className="flex flex-row items-center justify-center gap-2">
            <VideoMessage
              videoSrc={texto}
              caption={caption}
              onClick={() =>
                onOpenPreview({
                  type: 'video',
                  url: texto,
                  name: caption || 'video',
                  readonly: true
                })
              }
            />
          </div>
        )}

        {tipo === 'documento' && texto && !isImage && (
          <div className="flex flex-row items-center justify-center gap-2">
            <div className="flex flex-col">
              {isPreviewDoc ? (
                <div className="relative">
                  <div
                    className="absolute inset-0 z-10 cursor-pointer"
                    onClick={() => onOpenPreview({ type: 'documento', url: texto, name: fileName || undefined, readonly: true })}
                  />
                  <iframe src={texto} title={fileName || 'documento'} className="rounded-md max-w-full sm:max-w-xs my-1 bg-white relative z-0" style={{ width: 220, height: 180 }} />
                </div>
              ) : (
                <div
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => onOpenPreview({ type: 'documento', url: texto, name: fileName || undefined, readonly: true })}
                  title="Visualizar documento"
                >
                  <FileText size={32} className="text-green-600" />
                  {fileName ? (
                    <span className="text-sm text-gray-600 dark:text-gray-300 max-w-[150px] truncate">
                      {fileName}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400 max-w-[150px] truncate">
                      {isPdf ? 'Documento PDF' : 'Documento'}
                    </span>
                  )}
                </div>
              )}
              {caption && (
                <div className="flex flex-col">
                  <hr className="w-full border-gray-200" />
                  <p className="text-gray-800 dark:text-gray-100 break-words whitespace-pre-wrap">{renderTextWithLinks(caption)}</p>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 mt-1">
              {isPdf && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-gray-600 dark:text-gray-300"
                  title="Visualizar PDF"
                  onClick={() => handleViewPDF(texto, fileName || 'documento.pdf')}
                >
                  <Eye size={20} />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-600 dark:text-gray-300"
                title="Baixar arquivo"
                onClick={() => handleDownload(texto, fileName || 'arquivo')}
              >
                <Download size={20} />
              </Button>
            </div>
          </div>
        )}

        {tipo === 'documento' && texto && isImage && (
          <div className="flex flex-row items-center gap-2">
            <div className="flex flex-col">
              <img
                src={texto}
                alt="Imagem"
                className="rounded-md max-w-full sm:max-w-xs my-1 cursor-pointer"
                style={{ maxHeight: 180 }}
                onClick={() => onOpenPreview({ type: 'imagem', url: texto, name: caption || fileName || 'imagem', readonly: true })}
              />
              {caption && (
                <div className="flex flex-col gap-1">
                  <hr className="w-full border-gray-200" />
                  <p className="text-gray-800 dark:text-gray-100 break-words whitespace-pre-wrap">{renderTextWithLinks(caption)}</p>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-1">
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-600 dark:text-gray-300"
                title="Baixar imagem"
                onClick={() => handleDownload(texto, fileName || 'imagem')}
              >
                <Download size={20} />
              </Button>
            </div>
          </div>
        )}

        {tipo === 'imagem' && texto && (
          <div className="flex flex-row items-center gap-2">
            <div className="flex flex-col">
              <img
                src={texto}
                alt="Imagem"
                className="rounded-md max-w-full sm:max-w-xs my-1 cursor-pointer"
                style={{ maxHeight: 180 }}
                onClick={() => onOpenPreview({ type: 'imagem', url: texto, name: caption || 'imagem', readonly: true })}
              />
              {caption && (
                <div className="flex flex-col gap-1">
                  <hr className="w-full border-gray-200" />
                  <p className="text-gray-800 dark:text-gray-100 break-words whitespace-pre-wrap">{renderTextWithLinks(caption)}</p>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-1">
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-600 dark:text-gray-300"
                title="Baixar imagem"
                onClick={() => handleDownload(texto, fileName || 'imagem')}
              >
                <Download size={20} />
              </Button>
            </div>
          </div>
        )}

        {tipo === 'sticker' && texto && (
          <div className="flex flex-row items-center gap-2">
            <div className="flex flex-col">
              {console.log(`[FRONTEND] Renderizando sticker - tipo: ${tipo}, texto: ${texto}, msg.id: ${msg.id}`)}
              <img
                src={texto}
                alt="Sticker"
                className="rounded-md max-w-xs my-1 cursor-pointer"
                style={{ maxHeight: 120, maxWidth: 120 }}
                onClick={() => {
                  console.log(`[FRONTEND] Clicando no sticker para preview - url: ${texto}`);
                  onOpenPreview({ type: 'sticker', url: texto, name: 'sticker', readonly: true });
                }}
                onLoad={() => console.log(`[FRONTEND] Sticker carregado com sucesso - url: ${texto}`)}
                onError={(e) => console.error(`[FRONTEND] Erro ao carregar sticker - url: ${texto}`, e)}
              />
              {caption && (
                <div className="flex flex-col gap-1">
                  <hr className="w-full border-gray-200" />
                  <p className="text-gray-800 dark:text-gray-100 break-words whitespace-pre-wrap">{renderTextWithLinks(caption)}</p>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-1">
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-600 dark:text-gray-300"
                title="Baixar sticker"
                onClick={() => {
                  console.log(`[FRONTEND] Baixando sticker - url: ${texto}, nome: ${msg.nomeArquivo || 'sticker.webp'}`);
                  handleDownload(texto, msg.nomeArquivo || 'sticker.webp');
                }}
              >
                <Download size={20} />
              </Button>
            </div>
          </div>
        )}

        {tipo === 'contato' && texto && (() => {
          const whatsappPhone =
            msg.contactInfo?.telefone?.replace(/\D/g, '') ||
            extrairTelefoneDeVCard(texto) ||
            '';
          const phoneNumber = whatsappPhone || texto;
          const displayName = msg.contactInfo?.nome || 'Contato';
          const email = msg.contactInfo?.email || '';

          return (
            <div className="my-1 max-w-full sm:max-w-xs">
              <div className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden border border-gray-200 dark:border-slate-700 shadow-sm">
                <div className="flex items-center gap-3 p-3">
                  <Avatar className="h-12 w-12 shrink-0">
                    <AvatarImage
                      src={getFileUrl(msg.contactInfo?.avatarUrl || msg.midiaUrl)}
                      alt={msg.contactInfo?.nome || 'Contato'}
                    />
                    <AvatarFallback className="bg-[#25D366]/15 text-[#128C7E] dark:text-[#25D366] text-sm font-semibold">
                      {getInitials(displayName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[15px] truncate text-gray-900 dark:text-white">
                      {displayName}
                    </div>
                    <div className="text-[13px] text-gray-500 dark:text-gray-400 truncate">
                      {phoneNumber}
                    </div>
                    {email && (
                      <div className="text-xs text-gray-400 dark:text-gray-500 truncate">
                        {email}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center border-t border-gray-100 dark:border-slate-700 px-2 py-1.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="w-full justify-start font-semibold text-[#128C7E] dark:text-[#25D366] hover:text-[#075E54] hover:bg-[#25D366]/10 dark:hover:text-[#34d16e]"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (whatsappPhone && onStartChat) {
                        onStartChat(whatsappPhone);
                      }
                    }}
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Conversar
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}

        {['button', 'list', 'poll', 'carousel'].includes(tipo) && (
          <InteractiveMessage
            msg={msg}
            onOptionSelect={async (optionId, optionText) => {
              const remoteJid = conversa?.contato?.telefone
                ? `${conversa.contato.telefone}@s.whatsapp.net`
                : msg.remetente;
              const instanciaId = conversa?.instancia?.id || conversa?.instanciaUazapi?.id;
              try {
                await api.post('/mensagens/send-message', {
                  remoteJid,
                  type: 'texto',
                  text: optionText,
                  conversaId: msg.ticketId,
                  instanciaId,
                  mensagemRespondidaId: msg.id,
                });
              } catch (err: any) {
                toast({
                  title: "Erro",
                  description: err?.response?.data?.message || 'Falha ao enviar resposta',
                  variant: "destructive",
                });
              }
            }}
          />
        )}

        <div className={`text-xs text-right mt-1 flex items-center space-x-1 ${isAtendente ? 'text-gray-500 dark:text-gray-400' : 'text-gray-400'} ${isAtendente ? 'justify-end' : 'justify-start'}`}>
          {textoCopiavel && !msg.deletedAt && (
            <button
              onClick={(e) => { e.stopPropagation(); handleCopy(textoCopiavel); }}
              className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-gray-700 dark:hover:text-gray-200"
              title="Copiar mensagem"
            >
              <ClipboardCopy size={12} />
            </button>
          )}
          <span>{horaFormatada}</span>
          {renderStatusIcon()}
        </div>
      </div>

      {msg.remetenteTipo === 'cliente' && Array.isArray(msg.reactions) && msg.reactions.length > 0 && (
        <div className="flex items-center justify-end mb-1">
          {msg.reactions.map((reaction: any, index: number) => (
            <span key={index} className="mr-1">
              {renderEmojiWithFallback(reaction.conteudo)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Evita re-render: compara somente o necessário
  const a = prevProps.msg;
  const b = nextProps.msg;
  return (
    a.id === b.id &&
    a.status === b.status &&
    a.statusEntrega === b.statusEntrega &&
    a.conteudo === b.conteudo &&
    a.conteudoOriginal === b.conteudoOriginal &&
    a.dataEdicao === b.dataEdicao &&
    a.caption === b.caption &&
    a.tipo === b.tipo &&
    a.deletedAt === b.deletedAt &&
    (a.mensagemRespondida?.id || null) === (b.mensagemRespondida?.id || null) &&
    (Array.isArray(a.reactions) ? a.reactions.length : 0) === (Array.isArray(b.reactions) ? b.reactions.length : 0) &&
    prevProps.selectedMessage === nextProps.selectedMessage
  );
});

export default MensagemItem;